/**
 * ArrayEditors.tsx — Repeater für die Array-of-Objects-Felder der Dimensionen
 * (Qualifikationen, Top-Kunden nach Umsatzanteil). Arbeiten auf dem
 * serialisierten JSON-String (parseList/serializeList).
 */
import { parseList, serializeList } from '../../../lib/store/dimensionScoreInput';
import type { Option } from './FormFields';
import { Button } from '../../ui/Button';

interface Qualification {
  type: string;
  title: string;
  yearObtained: number | null;
  industryRelevant: boolean;
}

const ROW_INPUT =
  'rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-sm text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900';

const REMOVE_BTN =
  'font-sans text-xs font-medium uppercase tracking-widest text-stone-400 hover:text-rose-600';

export function QualificationsEditor(p: {
  readonly label: string;
  readonly hint?: string;
  readonly typeOptions: readonly Option[];
  readonly addLabel: string;
  readonly industryRelevantLabel: string;
  readonly value: string;
  readonly onChange: (serialized: string) => void;
}) {
  const items = parseList<Qualification>(p.value);
  const commit = (next: Qualification[]) => {
    p.onChange(serializeList(next));
  };
  const update = (i: number, patch: Partial<Qualification>) => {
    commit(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };

  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {p.label}
      </p>
      {p.hint && <p className="mt-2 font-serif text-sm italic text-stone-500">{p.hint}</p>}
      <div className="mt-3 space-y-3">
        {items.map((it, i) => (
          <div
            key={i}
            className="flex flex-col gap-3 rounded-sm border border-stone-200 bg-stone-50 p-3 sm:flex-row sm:items-center sm:flex-wrap"
          >
            <select
              aria-label="Art"
              value={it.type}
              onChange={(e) => {
                update(i, { type: e.target.value });
              }}
              className={ROW_INPUT}
            >
              <option value="">— Art —</option>
              {p.typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              aria-label="Bezeichnung"
              type="text"
              placeholder="Bezeichnung"
              value={it.title}
              onChange={(e) => {
                update(i, { title: e.target.value });
              }}
              className={`${ROW_INPUT} flex-1 min-w-[10rem]`}
            />
            <input
              aria-label="Jahr"
              type="number"
              placeholder="Jahr"
              value={it.yearObtained ?? ''}
              onChange={(e) => {
                update(i, { yearObtained: e.target.value === '' ? null : Number(e.target.value) });
              }}
              className={`${ROW_INPUT} w-24`}
            />
            <label className="inline-flex items-center gap-2 font-serif text-sm text-stone-700">
              <input
                type="checkbox"
                checked={it.industryRelevant}
                onChange={(e) => {
                  update(i, { industryRelevant: e.target.checked });
                }}
                className="h-4 w-4 accent-stone-900"
              />
              {p.industryRelevantLabel}
            </label>
            <button
              type="button"
              onClick={() => {
                commit(items.filter((_, idx) => idx !== i));
              }}
              className={REMOVE_BTN}
            >
              Entfernen
            </button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          commit([...items, { type: '', title: '', yearObtained: null, industryRelevant: false }]);
        }}
        className="mt-3"
      >
        + {p.addLabel}
      </Button>
    </div>
  );
}

interface TopClient {
  id: string;
  revenueSharePercent: number | null;
}

export function TopClientsEditor(p: {
  readonly label: string;
  readonly hint?: string;
  readonly addLabel: string;
  readonly value: string;
  readonly onChange: (serialized: string) => void;
}) {
  const items = parseList<TopClient>(p.value);
  const commit = (next: TopClient[]) => {
    p.onChange(serializeList(next));
  };
  const update = (i: number, patch: Partial<TopClient>) => {
    commit(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };

  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {p.label}
      </p>
      {p.hint && <p className="mt-2 font-serif text-sm italic text-stone-500">{p.hint}</p>}
      <div className="mt-3 space-y-3">
        {items.map((it, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3">
            <input
              aria-label="Kunde"
              type="text"
              placeholder="Kunde / Auftraggeber"
              value={it.id}
              onChange={(e) => {
                update(i, { id: e.target.value });
              }}
              className={`${ROW_INPUT} flex-1 min-w-[10rem]`}
            />
            <div className="flex items-center gap-2">
              <input
                aria-label="Umsatzanteil in Prozent"
                type="number"
                min={0}
                max={100}
                placeholder="Anteil"
                value={it.revenueSharePercent ?? ''}
                onChange={(e) => {
                  update(i, {
                    revenueSharePercent: e.target.value === '' ? null : Number(e.target.value),
                  });
                }}
                className={`${ROW_INPUT} w-24 tabular-nums`}
              />
              <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-400">
                % Umsatz
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                commit(items.filter((_, idx) => idx !== i));
              }}
              className={REMOVE_BTN}
            >
              Entfernen
            </button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          commit([...items, { id: '', revenueSharePercent: null }]);
        }}
        className="mt-3"
      >
        + {p.addLabel}
      </Button>
    </div>
  );
}
